import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, HostListener, inject, Input, OnInit, Output, Renderer2, DOCUMENT } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { TrackingEvents } from '../_shared/data/tracking-events.data';
import { DynamicElementService } from '../_shared/models/dynamic/dynamic-element-service.model';
import { IFormFieldGroup } from '../_shared/models/form/form-field-group.model';
import { IFormResponse } from '../_shared/models/form/form-response.model';
import { FormSubmissionData } from '../_shared/models/form/form-submission-data.model';
import { FormSubmission } from '../_shared/models/form/form-submission.model';
import { LegalConsentOptions } from '../_shared/models/form/legal-consent/legal-consent-options.model';
import { ITrackingParams } from '../_shared/models/tracking/tracking-params.model';
import { DynamicElementDefaultService } from '../_shared/services/dynamic-element-default.service';
import { GtmService } from '../_shared/services/gtm.service';
import { SnowplowService } from '../_shared/services/snowplow.service';
import { ViewService } from '../_shared/services/view.service';
import { ICustomWindow, WindowRefService } from '../_shared/services/window-ref.service';
import { createForm } from '../_shared/types/snowtype/snowplow';
import { ButtonComponent } from '../button/button.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { TextHtmlComponent } from '../text-html/text-html.component';
import { DynamicFormElementDirective } from './_directives/dynamic-form-element.directive';
import { FormClientBase } from './form-client/form-client-base';
import { FormClientSalesforce } from './form-client/form-client-salesforce';
import { FormClientSalesforceMatchmaking } from './form-client/form-client-salesforce-matchmaking';
import { ConversionApiHelperService } from './form-conversion-api/form-conversion-api-helper.service';
import { ConversionApiData } from './form-conversion-api/form-conversion-api.data';
import { FormService } from './form.service';

@Component({
    selector: 'sirius-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        ButtonComponent,
        SpinnerComponent,
        TranslateModule,
        DynamicFormElementDirective,
        TextHtmlComponent,
    ],
    providers: [
        {
            provide: DynamicElementService,
            useClass: DynamicElementDefaultService,
        },
    ]
})
export class FormComponent implements OnInit, AfterViewInit {
    private _document = inject<Document>(DOCUMENT);
    private _formService = inject(FormService);
    private _viewService = inject(ViewService);
    private _elRef = inject(ElementRef);
    private _renderer = inject(Renderer2);
    private _httpClient = inject(HttpClient);
    private _gtmService = inject(GtmService);
    private _snowplowService = inject(SnowplowService);

    @Input() formElementGroups: IFormFieldGroup[] = [];
    @Input() name = '';
    @Input() id = uuidv4();
    @Input() recaptchaKey = '';
    @Input() recaptchaToken = '';
    @Input() buttonText = '';
    @Input() hasButtonIcon = true;
    @Input() hasCancelButton = false;
    @Input() cancelButtonText = '';
    @Input() consent$?: Observable<LegalConsentOptions>;
    @Input() action = '';
    @Input() infoHtmlKey = '';
    @Input() clientName = '';
    @Input() client!: FormClientBase;
    @Input() form = new FormGroup({});
    /**
     * Public input API for setting field errors from outside Angular (e.g. in the MCAE layout iframe).
     *
     * Note: Angular Elements exposes component inputs/outputs on the custom element, but not arbitrary
     * component instance methods. Therefore, layout.html must set this property (not call a method).
     */
    @Input() set externalErrors(errors: { field: string; error: Record<string, boolean> }[] | null | undefined) {
        this.applyErrors(errors ?? []);
    }

    @Output() submitPressed = new EventEmitter<void>();
    @Output() cancelPressed = new EventEmitter<void>();
    @Output() successEvent = new EventEmitter<IFormResponse>();
    @Output() errorEvent = new EventEmitter<IFormResponse>();

    response?: IFormResponse;
    tracking: ITrackingParams = {
        parameters: { identifier: 'form_user_data/button_primary' },
    };

    private _isSubmitting = new BehaviorSubject<boolean>(false);
    isSubmitting$ = this._isSubmitting.asObservable();

    private _inView = new BehaviorSubject<boolean>(false);
    private _dirty = false;
    private _submitError = new Subject<string>();
    private _submitComplete = new Subject<void>();
    private _submit = new Subject<void>();
    private _submissionData = new FormSubmissionData();
    private _touched = false;
    private _conversionApiHelper?: ConversionApiHelperService;
    private _windowRef: WindowRefService;
    private _cdr = inject(ChangeDetectorRef);
    private _destroyRef = inject(DestroyRef);

    private get Window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    get success() {
        return this.response?.type === 'success' && this.response?.message;
    }

    get error() {
        return this.response?.type === 'error' && this.response?.message;
    }

    constructor() {
        const windowRef = inject(WindowRefService);

        this._windowRef = windowRef;
    }

    @HostListener('window:scroll', [])
    onScroll(): void {
        this.checkFormInView();
    }

    @HostListener('touchstart')
    @HostListener('click')
    @HostListener('keydown')
    onTouched(): void {
        this._touched = true;
    }

    ngOnInit(): void {
        const fields = this.formElementGroups.flatMap((x: IFormFieldGroup) => x.formFields ?? []);
        this.form = this._formService.createForm(fields);
        this._conversionApiHelper = new ConversionApiHelperService(fields);

        if (this.clientName === 'matchmaking') {
            this.client = new FormClientSalesforceMatchmaking(
                this._httpClient,
                this._windowRef,
                this._conversionApiHelper
            );
        } else {
            this.client = new FormClientSalesforce(this._httpClient, this._windowRef);
        }

        const searchParams = new URLSearchParams(this._document.location.search);
        const id = searchParams.get('formId');

        if (id) {
            this.id = id;
        }

        this.consent$?.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: LegalConsentOptions) => {
            this._submit.next();
            this.submit(x);
        });
    }

    ngAfterViewInit(): void {
        const el = this._elRef.nativeElement;
        this._renderer.setStyle(el, '--form-response-height', `${el.offsetHeight}px`, 2);

        if (!this.Window) {
            return;
        }

        this.Window.postMessage({ done: { sirForm: this.id } }, '*');
        this.checkFormInView();
        this.addTracking();
    }

    onSubmit(): void {
        this._formService.validateForm(this.form);

        if (!this.form.valid) {
            this._submitError.next('form_field_validation');

            return;
        }

        if (this.consent$) {
            this.submitPressed.emit();

            return;
        }

        this._submit.next();
        this.submit();
    }

    onCancel(): void {
        this.cancelPressed.emit();
    }

    /**
     * Sets validation errors on form controls from outside Angular's zone.
     * Triggers change detection so that OnPush components reflect the updated state.
     * Used by layout.html (vanilla JS) after Pardot returns field-level errors.
     */
    setErrors(errors: { field: string; error: Record<string, boolean> }[]): void {
        this.applyErrors(errors);
    }

    private applyErrors(errors: { field: string; error: Record<string, boolean> }[]): void {
        errors.forEach(({ field, error }) => {
            const control = this.form.get(field);
            if (control) {
                control.setErrors(error);
                control.markAsTouched({ onlySelf: true });
            }
        });
        this._cdr.markForCheck();
    }

    private submit(consentOpts?: LegalConsentOptions) {
        this._submissionData = this._formService.createFormSubmissionData(this.form.value, consentOpts);

        this.client.submission = new FormSubmission({
            data: this._submissionData,
            apiUrl: this.action,
            recaptchaKey: this.recaptchaKey,
            recaptchaToken: this.recaptchaToken,
        });

        this.addClientEventHandlers();
        this.client.submit();
    }

    private addClientEventHandlers() {
        this.client.error$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: IFormResponse) => {
            this.response = x;

            this._submitError.next('server_error');
            this.errorEvent.emit(x);
        });

        this.client.complete$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            this._submitComplete.next();
        });

        this.client.success$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: IFormResponse) => {
            this.response = x;

            this.successEvent.emit(x);

            if (x.redirectUri) this._renderer.setProperty(this._document.location, 'href', x.redirectUri);
        });

        this.client.isSubmitting$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: boolean) => {
            this._isSubmitting.next(x);
        });
    }

    private checkFormInView(): void {
        const inView = this._viewService.isTopInView(this._elRef.nativeElement);

        if (this._inView.getValue() || !inView) return;

        this._inView.next(true);
    }

    //#region tracking
    private addTracking() {
        const cfg: { [key: string]: string } = {
            name: this.name.toLowerCase().trim(),
            category: this._formService.getFormCategory(this.name).toLowerCase().trim(),
        };

        const formEntity = createForm({
            name: cfg['name'],
            category: cfg['category'],
        });

        this._inView.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: boolean) => {
            if (!x) return;

            this.trackEvent('formView', [cfg]);

            const cfg2 = {
                context: [formEntity],
            };

            this._snowplowService.track('formView', cfg2);
        });

        this.form.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            if (this._dirty || !this._touched) return;

            this.trackEvent('formStart', [cfg]);

            const cfg2 = {
                context: [formEntity],
            };

            this._snowplowService.track('formStart', cfg2);

            this._dirty = true;
        });

        this._submit.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
            this.trackEvent('formSubmit', [cfg]);

            const cfg2 = {
                context: [formEntity],
            };

            this._snowplowService.track('formSubmit', cfg2);
        });

        this._submitError.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((x: string) => {
            cfg['type'] = x;

            this.trackEvent('formError', [cfg]);

            const cfg2 = {
                type: x,
                context: [formEntity],
            };

            this._snowplowService.track('formError', cfg2);
        });

        this._submitComplete.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(async () => {
            if (!this._conversionApiHelper) return;

            this._conversionApiHelper.submissionData = this._submissionData;

            const eventData = await this._conversionApiHelper.getNormalizedObj(ConversionApiData.eventData);
            const eventCfg = { ...cfg, ...eventData };

            const userData = await this._conversionApiHelper.getNormalizedObj(
                ConversionApiData.userData,
                'sha256_country'
            );
            const userCfg = userData;

            this.trackEvent('formComplete', [eventCfg, userCfg]);

            const cfg2 = {
                intent: eventCfg['intent'] ?? null,
                timeline: eventCfg['timeline'] ?? null,
                application_type: eventCfg['application_type'] ?? null,
                sha256_country: userCfg['sha256_country'] ?? null,
                sha256_email: userCfg['sha256_email'] ?? null,
                sha256_first_name: userCfg['sha256_first_name'] ?? null,
                sha256_last_name: userCfg['sha256_last_name'] ?? null,
                sha256_phone: userCfg['sha256_phone'] ?? null,
                sha256_phone_e164: userCfg['sha256_phone_e164'] ?? null,
                sha256_state: userCfg['sha256_state'] ?? null,
                sha256_zipcode: userCfg['sha256_postal_code'] ?? null,
                role: userCfg['role'] ?? null,
                role_subcategory: userCfg['role_subcategory'] ?? null,
                industry: userCfg['industry'] ?? null,
                country: userCfg['country'] ?? null,
                state: userCfg['state'] ?? null,
                first_name: userCfg['first_name'] ?? null,
                last_name: userCfg['last_name'] ?? null,
                email: userCfg['email'] ?? null,
                phone: userCfg['phone'] ?? null,
                zipcode: userCfg['zip'] ?? null,
                context: [formEntity],
            };

            this._snowplowService.track('formComplete', cfg2);
        });
    }

    private trackEvent(key: string, cfg: unknown[]) {
        this._gtmService.track(TrackingEvents.gtm[key](cfg));
    }
    //#endregion
}
